import React, { useState } from "react";
import { FaBan, FaBirthdayCake, FaCalendarAlt, FaCheckCircle, FaClock, FaEye, FaTimes, FaUsers } from "react-icons/fa";
import api from "../api";
import { sanitizeDocumentTemplateHtml } from "../security/documentTemplateHtml";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("pt-BR");
};

const statusMeta = (status) => {
  if (status === "SENT") return ["Enviado", "bg-emerald-50 text-emerald-700"];
  if (status === "FAILED") return ["Falhou", "bg-rose-50 text-rose-700"];
  if (status === "CANCELED") return ["Cancelado", "bg-slate-100 text-slate-600"];
  if (status === "PROCESSING") return ["Processando", "bg-blue-50 text-blue-700"];
  return ["Agendado", "bg-amber-50 text-amber-700"];
};

const birthdaySourceLabel = (source) => ({
  HR: "Recursos Humanos",
  STUDENT: "Alunos",
  ATTENDEE: "Atendidos",
}[String(source || "").toUpperCase()] || "Módulo");

const audienceStatusLabel = (status) => ({
  ACTIVE: "Cadastros ativos",
  INACTIVE: "Cadastros inativos",
  ALL: "Todos os cadastros",
}[String(status || "").toUpperCase()] || "Público automático");

const birthdayMeta = (item) => item?.result?.scheduleMeta?.birthday || null;

export default function CRMEmailMarketingSchedules({ schedules = [], onReload }) {
  const [preview, setPreview] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [feedback, setFeedback] = useState("");

  const birthdayGroups = Object.values(
    schedules.reduce((groups, item) => {
      const meta = birthdayMeta(item);
      if (!meta?.source) return groups;
      const key = String(meta.source).toUpperCase();
      if (!groups[key]) groups[key] = { source: key, items: [] };
      groups[key].items.push(item);
      return groups;
    }, {}),
  ).map((group) => {
    const scheduled = group.items.filter((item) => item.status === "SCHEDULED");
    const sent = group.items.filter((item) => item.status === "SENT");
    const failed = group.items.filter((item) => item.status === "FAILED");
    const canceled = group.items.filter((item) => item.status === "CANCELED");
    const upcoming = [...scheduled].sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
    const audienceStatus =
      birthdayMeta(upcoming[0])?.audienceStatus ||
      birthdayMeta(group.items[0])?.audienceStatus ||
      "";
    return {
      ...group,
      scheduled,
      sent,
      failed,
      canceled,
      next: upcoming[0] || null,
      audienceStatus,
    };
  });

  const regularSchedules = schedules.filter((item) => !birthdayMeta(item));

  const cancel = async (item) => {
    if (!window.confirm("Cancelar este agendamento?")) return;
    setBusyId(item.id);
    setFeedback("");
    try {
      await api.delete("/mail/marketing/schedules/" + item.id);
      setFeedback("Agendamento cancelado.");
      await onReload?.();
    } catch (error) {
      setFeedback(error.response?.data?.error || "Não foi possível cancelar o agendamento.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="space-y-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-50 text-blue-700"><FaCalendarAlt /></span>
        <div>
          <h3 className="text-lg font-black text-slate-900">Agendamentos</h3>
          <p className="text-xs text-slate-500">Automações por módulo aparecem agrupadas; campanhas avulsas continuam listadas individualmente.</p>
        </div>
      </div>

      {feedback && <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700">{feedback}</div>}

      {birthdayGroups.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 px-1">
            <FaBirthdayCake className="text-blue-600" />
            <strong className="text-sm text-slate-800">Automações de aniversário por módulo</strong>
            <span className="text-xs text-slate-400">Você configura uma vez; o sistema mantém os destinatários sozinho.</span>
          </div>
          <div className="grid gap-3 lg:grid-cols-3">
            {birthdayGroups.map((group) => (
              <article key={group.source} className="overflow-hidden rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50/70 via-white to-white">
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="grid h-10 w-10 place-items-center rounded-xl bg-blue-600 text-white"><FaBirthdayCake /></span>
                      <div>
                        <span className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-blue-500">Automação do módulo</span>
                        <h4 className="mt-0.5 font-black text-slate-900">{birthdaySourceLabel(group.source)}</h4>
                      </div>
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-extrabold ${group.scheduled.length ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {group.scheduled.length ? "Ativa" : "Sem próximos envios"}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-xl border border-slate-100 bg-white p-3">
                      <span className="block text-[9px] font-extrabold uppercase tracking-wide text-slate-400">Público</span>
                      <strong className="mt-1 block text-xs text-slate-700">{audienceStatusLabel(group.audienceStatus)}</strong>
                    </div>
                    <div className="rounded-xl border border-slate-100 bg-white p-3">
                      <span className="block text-[9px] font-extrabold uppercase tracking-wide text-slate-400">Próximos envios</span>
                      <strong className="mt-1 flex items-center gap-1 text-xs text-slate-700"><FaUsers className="text-blue-500" /> {group.scheduled.length}</strong>
                    </div>
                  </div>

                  <div className="mt-3 rounded-xl bg-white px-3 py-2.5 text-xs text-slate-500">
                    {group.next ? (
                      <span className="inline-flex items-center gap-1.5"><FaClock className="text-blue-500" /> Próximo aniversário: <strong className="text-slate-700">{formatDateTime(group.next.scheduledAt)}</strong></span>
                    ) : (
                      <span>Nenhum destinatário elegível agendado neste momento.</span>
                    )}
                  </div>

                  {(group.sent.length > 0 || group.failed.length > 0) && (
                    <div className="mt-2 flex flex-wrap gap-2 text-[10px] font-bold">
                      {group.sent.length > 0 && <span className="rounded-full bg-emerald-50 px-2 py-1 text-emerald-700">{group.sent.length} enviado(s)</span>}
                      {group.failed.length > 0 && <span className="rounded-full bg-rose-50 px-2 py-1 text-rose-700">{group.failed.length} falha(s)</span>}
                    </div>
                  )}
                </div>
                <div className="border-t border-blue-100 bg-blue-50/50 px-4 py-2.5 text-[10px] font-semibold text-blue-700">
                  Gerenciado automaticamente pela aba Aniversariantes. Não é necessário agendar pessoa por pessoa.
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {birthdayGroups.length > 0 && <div className="border-t border-slate-100" />}

      <div className="grid gap-3 lg:grid-cols-2">
        {regularSchedules.map((item) => {
          const [label, className] = statusMeta(item.status);
          return (
            <article key={item.id} className="rounded-2xl border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-400">{item.theme || "Campanha"}</span>
                  <h4 className="mt-1 truncate font-black text-slate-900">{item.subject}</h4>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><FaClock /> {formatDateTime(item.scheduledAt)}</span>
                    <span>{Array.isArray(item.recipients) ? item.recipients.length : 0} destinatário(s)</span>
                  </div>
                </div>
                <span className={"shrink-0 rounded-full px-2.5 py-1 text-xs font-extrabold " + className}>{label}</span>
              </div>

              {item.error && <div className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{item.error}</div>}
              {item.status === "SENT" && (
                <div className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
                  <FaCheckCircle /> Enviado em {formatDateTime(item.sentAt)}
                </div>
              )}
              {item.status === "CANCELED" && (
                <div className="mt-3 flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                  <FaBan /> Cancelado em {formatDateTime(item.canceledAt)}
                </div>
              )}

              <div className="mt-4 flex justify-end gap-2">
                <button type="button" onClick={() => setPreview(item)} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-2 text-xs font-extrabold text-slate-600"><FaEye /> Ver conteúdo</button>
                {item.status === "SCHEDULED" && (
                  <button type="button" onClick={() => cancel(item)} disabled={busyId === item.id} className="rounded-full border border-rose-200 px-3 py-2 text-xs font-extrabold text-rose-600 disabled:opacity-50">Cancelar</button>
                )}
              </div>
            </article>
          );
        })}
        {!regularSchedules.length && !birthdayGroups.length && <div className="lg:col-span-2 rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm font-semibold text-slate-400">Nenhuma campanha agendada.</div>}
        {!regularSchedules.length && birthdayGroups.length > 0 && <div className="lg:col-span-2 rounded-2xl border border-dashed border-slate-200 p-6 text-center text-xs font-semibold text-slate-400">Nenhuma campanha avulsa agendada. As automações de aniversário estão resumidas acima.</div>}
      </div>

      {preview && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm" onMouseDown={() => setPreview(null)}>
          <div className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-slate-100 p-5 shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" aria-label="Fechar conteúdo" onClick={() => setPreview(null)} className="absolute right-7 top-7 z-20 grid h-9 w-9 place-items-center rounded-full border border-white/30 bg-slate-950/55 text-white"><FaTimes /></button>
            <div className="mx-auto overflow-hidden rounded-2xl bg-white shadow-sm">
              <div className="bg-[#071f54] px-7 py-5 pr-16 text-lg font-black text-white">{preview.theme || preview.subject}</div>
              <div className="p-8">
                <div className="mb-4 rounded-xl bg-slate-50 p-3 text-xs text-slate-500">
                  <strong className="block text-slate-700">Agendado para {formatDateTime(preview.scheduledAt)}</strong>
                  <span className="mt-1 block break-words">{(preview.recipients || []).join(", ")}</span>
                </div>
                <h3 className="text-xl font-black text-slate-900">{preview.subject}</h3>
                <div className="prose mt-6 max-w-none text-slate-800" dangerouslySetInnerHTML={{ __html: sanitizeDocumentTemplateHtml(preview.html || "") }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
